import { useReduxState } from 'hooks';
import { EditorBlockTranslationReference } from 'components/editor';
import { RichTextValue } from 'components/common';
import { Form, FormGroup, Checkbox } from 'components/common/form';
import { EditorBlockTextField } from 'components/editor/blocks';
import { TextEditor } from 'components/editor/inputs';

import type { BlockDiscussion } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockDiscussion = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockDiscussion> & StateProps) => {
  const { messages, theme } = useReduxState();

  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const handleTitleChange = (title: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            title
          }
        }
      }
    });
  };

  const handleTextChange = (text: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            text
          }
        }
      }
    });
  };

  const handleAllowAnonymousChange = (allowAnonymousComments: boolean) => {
    updateData({
      ...data,
      allowAnonymousComments
    });
  };

  return (
    <div className="discussion">
      <div className="title">
        {isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.title || ''}
            style={{
              color: theme.textColorLight,
              fontSize: '1.5rem',
              fontWeight: 700,
              padding: '10px 15px'
            }}
          />
        ) : (
          <div style={{ padding: '10px 15px' }}>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGreyDark}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.title || ''
              }
              translationHtml={
                data.locales[languageVersion].translatable.title || ''
              }
            >
              <EditorBlockTextField
                aria-label={messages.editorBlockDiscussionTitle}
                onValueChange={handleTitleChange}
                placeholder={messages.readerDiscussion}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: theme.textColorLight,
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  padding: 0
                }}
                value={data.locales[languageVersion].translatable.title || ''}
              />
            </EditorBlockTranslationReference>
          </div>
        )}
      </div>
      <div className="content">
        {isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.text}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.text || ''
              }
              translationHtml={data.locales[languageVersion].translatable.text}
            >
              <TextEditor
                backgroundColor="inherit"
                content={data.locales[languageVersion].translatable.text}
                onChange={handleTextChange}
                padding="0"
                placeholder={messages.editorBlockDiscussionPlaceholder}
              />
            </EditorBlockTranslationReference>
          </>
        )}
        <FormGroup
          label={messages.commonComments}
          maxWidth="100%"
          style={{ marginTop: 30 }}
        >
          <Form
            initialValues={{
              allowAnonymousComments: data.allowAnonymousComments
            }}
          >
            <Checkbox
              disabled={isPreview || Boolean(editorLanguageActive)}
              field="allowAnonymousComments"
              id={`editor-block-discussion-allow-anonymous-${blockId}`}
              label={messages.editorBlockDiscussionAllowAnonymousComments}
              onValueChange={handleAllowAnonymousChange}
            />
          </Form>
        </FormGroup>
      </div>

      <style jsx>{`
        .discussion {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
        }
        .title {
          background-color: ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0;
        }
        .content {
          color: ${theme.textColorLight};
          line-height: 1.4;
          padding: 15px 15px 18px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockDiscussion;
